import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Fade,
  List,
  ListItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import useAudioLevel from './useAudioLevel';
import VoiceLevelIndicator from './VoiceLevelIndicator';

const MIC_CONSTRAINTS = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    channelCount: 1,
  },
};

const MIC_LEVEL_SPOKEN_THRESHOLD = 0.05;

function ScmapPreflightGate({ onComplete }) {
  const [step, setStep] = useState('checking_permission');
  const [micTestActive, setMicTestActive] = useState(false);
  const [speakerPlayed, setSpeakerPlayed] = useState(false);
  const [speakerFailed, setSpeakerFailed] = useState(false);
  const [error, setError] = useState(null);

  const audioCtxRef = useRef(null);
  const permissionStreamRef = useRef(null);

  const micLevel = useAudioLevel(micTestActive);

  const stopPermissionStream = useCallback(() => {
    permissionStreamRef.current?.getTracks().forEach((track) => track.stop());
    permissionStreamRef.current = null;
  }, []);

  const acquirePermissionStream = useCallback(async () => {
    stopPermissionStream();
    permissionStreamRef.current = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
    return permissionStreamRef.current;
  }, [stopPermissionStream]);

  useEffect(() => {
    let cancelled = false;

    const detectPermission = async () => {
      setStep('checking_permission');

      try {
        if (navigator.permissions?.query) {
          const status = await navigator.permissions.query({ name: 'microphone' });
          if (cancelled) return;

          if (status.state === 'granted') {
            await acquirePermissionStream();
            if (cancelled) return;
            setStep('mic_test');
            setMicTestActive(true);
          } else if (status.state === 'denied') {
            setStep('denied');
          } else {
            setStep('request_permission');
          }
        } else {
          setStep('request_permission');
        }
      } catch (permissionError) {
        console.warn('[ScmapPreflightGate] Permission detection failed:', permissionError);
        if (!cancelled) {
          setStep('request_permission');
        }
      }
    };

    detectPermission();

    return () => {
      cancelled = true;
      setMicTestActive(false);
      stopPermissionStream();
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        audioCtxRef.current.close().catch((closeError) => {
          console.warn('[ScmapPreflightGate] AudioContext close failed:', closeError);
        });
      }
    };
  }, [acquirePermissionStream, stopPermissionStream]);

  const handleRequestPermission = useCallback(async () => {
    try {
      await acquirePermissionStream();
      setError(null);
      setStep('mic_test');
      setMicTestActive(true);
    } catch (requestError) {
      if (
        requestError?.name === 'NotAllowedError' ||
        requestError?.name === 'PermissionDeniedError'
      ) {
        setStep('denied');
      } else {
        setError(requestError?.message || 'Could not access microphone');
      }
    }
  }, [acquirePermissionStream]);

  const handleMicConfirmed = useCallback(() => {
    setMicTestActive(false);
    stopPermissionStream();
    setSpeakerPlayed(false);
    setSpeakerFailed(false);
    setStep('speaker_test');
  }, [stopPermissionStream]);

  const handlePlayTone = useCallback(async () => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) {
        console.warn('[ScmapPreflightGate] Web Audio API not available.');
        setSpeakerPlayed(true);
        return;
      }

      if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
        audioCtxRef.current = new AudioContextClass();
      }

      if (audioCtxRef.current.state === 'suspended') {
        await audioCtxRef.current.resume();
      }

      const silentBuffer = audioCtxRef.current.createBuffer(1, 1, 22050);
      const silentSource = audioCtxRef.current.createBufferSource();
      silentSource.buffer = silentBuffer;
      silentSource.connect(audioCtxRef.current.destination);
      silentSource.start(0);

      const oscillator = audioCtxRef.current.createOscillator();
      const gainNode = audioCtxRef.current.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(520, audioCtxRef.current.currentTime);
      gainNode.gain.setValueAtTime(0, audioCtxRef.current.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.18, audioCtxRef.current.currentTime + 0.05);
      gainNode.gain.linearRampToValueAtTime(0, audioCtxRef.current.currentTime + 0.45);
      oscillator.connect(gainNode);
      gainNode.connect(audioCtxRef.current.destination);
      oscillator.start(audioCtxRef.current.currentTime);
      oscillator.stop(audioCtxRef.current.currentTime + 0.5);

      setSpeakerPlayed(true);
    } catch (toneError) {
      console.warn('[ScmapPreflightGate] Speaker tone failed:', toneError);
      setSpeakerPlayed(true);
    }
  }, []);

  const handleComplete = useCallback(() => {
    onComplete?.();
  }, [onComplete]);

  const renderStepContent = () => {
    if (step === 'checking_permission') {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <CircularProgress size={28} />
          <Typography color="text.secondary">Getting ready…</Typography>
        </Box>
      );
    }

    if (step === 'request_permission') {
      return (
        <Paper elevation={2} sx={{ p: 4, borderRadius: 3, maxWidth: 440, width: '100%' }}>
          <Stack spacing={2} alignItems="center">
            <MicIcon sx={{ fontSize: 44, color: 'primary.main' }} />
            <Typography variant="h6" textAlign="center">
              Let&apos;s check your microphone
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              This symptom checker uses your voice. Tap below and select Allow when your browser
              asks for microphone access.
            </Typography>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={<MicIcon />}
              onClick={handleRequestPermission}
            >
              Enable microphone
            </Button>
          </Stack>
        </Paper>
      );
    }

    if (step === 'denied') {
      return (
        <Paper elevation={2} sx={{ p: 4, borderRadius: 3, maxWidth: 440, width: '100%' }}>
          <Stack spacing={2} alignItems="center">
            <MicOffIcon sx={{ fontSize: 44, color: 'error.main' }} />
            <Typography variant="h6" textAlign="center">
              Microphone access is blocked
            </Typography>
            <Typography variant="body2" color="text.secondary">
              To fix this:
            </Typography>
            <List dense sx={{ width: '100%' }}>
              <ListItem sx={{ px: 0 }}>
                1. Click the lock or camera icon in your browser&apos;s address bar
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                2. Set Microphone to Allow
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                3. Refresh the page and try again
              </ListItem>
            </List>
            <Button
              variant="contained"
              color="error"
              fullWidth
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
            >
              Refresh page
            </Button>
          </Stack>
        </Paper>
      );
    }

    if (step === 'mic_test') {
      return (
        <Paper elevation={2} sx={{ p: 4, borderRadius: 3, maxWidth: 440, width: '100%' }}>
          <Stack spacing={2} alignItems="center">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'scmapMicPulse 1.8s ease-in-out infinite',
                '@keyframes scmapMicPulse': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.08)' },
                  '100%': { transform: 'scale(1)' },
                },
              }}
            >
              <MicIcon sx={{ fontSize: 44, color: 'primary.main' }} />
            </Box>
            <Typography variant="h6" textAlign="center">
              Say something
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              The bars move when your microphone picks up your voice.
            </Typography>
            <VoiceLevelIndicator
              level={micLevel}
              barCount={7}
              height={36}
              userHasSpoken={micLevel > MIC_LEVEL_SPOKEN_THRESHOLD}
            />
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={<CheckCircleIcon />}
              onClick={handleMicConfirmed}
            >
              Microphone is working
            </Button>
            <Typography variant="caption" color="text.secondary" textAlign="center">
              Nothing moving? Check that your mic isn&apos;t muted.
            </Typography>
          </Stack>
        </Paper>
      );
    }

    if (step === 'speaker_test') {
      return (
        <Paper elevation={2} sx={{ p: 4, borderRadius: 3, maxWidth: 440, width: '100%' }}>
          <Stack spacing={2} alignItems="center">
            <VolumeUpIcon sx={{ fontSize: 44, color: 'primary.main' }} />
            <Typography variant="h6" textAlign="center">
              Can you hear a sound?
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Turn up your volume and tap the button.
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              size="large"
              startIcon={<VolumeUpIcon />}
              onClick={handlePlayTone}
            >
              {speakerPlayed ? 'Play again' : 'Play sound'}
            </Button>

            <Fade in={speakerPlayed}>
              <Stack spacing={1} width="100%">
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<CheckCircleIcon />}
                  onClick={handleComplete}
                >
                  Yes, I can hear it
                </Button>
                <Button
                  variant="text"
                  fullWidth
                  color="inherit"
                  onClick={() => setSpeakerFailed(true)}
                  sx={{ color: 'text.secondary' }}
                >
                  I can&apos;t hear anything
                </Button>
              </Stack>
            </Fade>

            <Fade in={speakerFailed}>
              <Stack spacing={1} width="100%">
                <Alert severity="warning" sx={{ textAlign: 'left' }}>
                  Try: check your system volume, ensure headphones are connected, or try a
                  different browser. You can still continue.
                </Alert>
                <Button variant="outlined" fullWidth onClick={handleComplete}>
                  Continue anyway
                </Button>
              </Stack>
            </Fade>
          </Stack>
        </Paper>
      );
    }

    return null;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        py: 3,
      }}
    >
      <Fade in key={step} timeout={300}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          {renderStepContent()}
        </Box>
      </Fade>
    </Box>
  );
}

export default ScmapPreflightGate;
